import React, { createContext, useContext, useMemo, useRef } from 'react';
import _ from 'lodash';

import FilterPlatesProvider from 'contextProviders/FilterPlatesProvider';
import FilterPlatesContext from 'contexts/FilterPlatesContext';

// The canvas's filters, which every FilterScope applies ahead of its own
export const CanvasFiltersContext = createContext<FilterPlateType[]>([]);

// On a report, the report's run-time filters, which a Composite's charts
// take (and set, when one is clicked) as the report's own gadgets do
interface InheritedDrillDowns {
  drillDowns: FilterPlateType[];
  setDrillDowns: React.Dispatch<React.SetStateAction<FilterPlateType[]>>;
}
const InheritedDrillDownsContext = createContext<
  InheritedDrillDowns | undefined
>(undefined);

// The same array while its contents don't change. Filters are rebuilt on each
// render (e.g. a Composite element's block + own filters), and a new array
// would flow through to a new filter input and refetch.
export const useStableFilters = (
  filters: FilterPlateType[],
): FilterPlateType[] => {
  const ref = useRef(filters);
  if (!_.isEqual(ref.current, filters)) {
    ref.current = filters;
  }
  return ref.current;
};

// Scopes a block's (or element's) queries to the canvas's filters and then
// its own. They're a scope rather than drill-downs, so a chart grouped by a
// filtered field still honours the filter. A nested FilterPlatesProvider
// replaces its parent's scope rather than adding to it, which is why the
// canvas filters are prepended here instead of wrapping the whole grid.
// A dashboard block's run-time filters are drill-downs, as on the dashboard.
// Always provided, even when empty: a chart clicked outside any provider
// would drill down through the context default, which has no setDrillDowns.
// Here clicking drills nowhere (Studio blocks aren't interactive), except
// under a report's inherited run-time filters, which a click adds to.
// A report block can ignore the canvas's filters, and any block can skip the
// default dataset filters (see GadgetSettingsScope).
export const FilterScope = ({
  filters,
  drillDowns,
  isCanvasFiltersIgnored = false,
  isDefaultFiltersDisabled = false,
  children,
}: {
  filters: FilterPlateType[];
  drillDowns?: FilterPlateType[];
  isCanvasFiltersIgnored?: boolean;
  isDefaultFiltersDisabled?: boolean;
  children: JSX.Element;
}) => {
  const canvasFilters = useContext(CanvasFiltersContext);
  const inherited = useContext(InheritedDrillDownsContext);
  const stableFilters = useStableFilters(
    isCanvasFiltersIgnored ? filters : [...canvasFilters, ...filters],
  );
  // A click rewrites the whole list from the one in scope here, so the
  // report's are only taken by a block without run-time filters of its own
  const drillsFrom = drillDowns ? undefined : inherited;
  const stableDrillDowns = useStableFilters(
    drillDowns ?? (drillsFrom ? drillsFrom.drillDowns : window.emptyArray),
  );
  return (
    <FilterPlatesProvider
      scope={stableFilters}
      drillDowns={stableDrillDowns}
      setDrillDowns={drillsFrom ? drillsFrom.setDrillDowns : undefined}
      isDefaultFiltersDisabled={isDefaultFiltersDisabled}
    >
      {children}
    </FilterPlatesProvider>
  );
};

// On a dashboard or report, Studio blocks without a gadget of their own
// (Composites, Text) take the filters around them (the report's, and
// run-time filters) as their canvas filters, since each FilterScope
// replaces the filters it sits in rather than adding to them. A drillable
// block (a Composite on a report) keeps the run-time filters apart, as its
// charts' run-time filters, so clicking one drills the report and the
// clicked chart highlights its term rather than being filtered down to it.
export const InheritedCanvasFilters = ({
  isDrillable = false,
  children,
}: {
  isDrillable?: boolean;
  children: JSX.Element;
}) => {
  const { scope, drillDowns, setDrillDowns } = useContext(FilterPlatesContext);
  const filters = useStableFilters([
    ...(scope ?? []),
    ...(isDrillable ? [] : drillDowns ?? []),
  ]);
  const inherited = useMemo(
    (): InheritedDrillDowns | undefined =>
      isDrillable
        ? { drillDowns: drillDowns ?? window.emptyArray, setDrillDowns }
        : undefined,
    [drillDowns, isDrillable, setDrillDowns],
  );
  return (
    <CanvasFiltersContext.Provider value={filters}>
      <InheritedDrillDownsContext.Provider value={inherited}>
        {children}
      </InheritedDrillDownsContext.Provider>
    </CanvasFiltersContext.Provider>
  );
};
